'use client'

import NextLink, { LinkProps } from 'next/link'
import { useSearchParams } from 'next/navigation'
import { AnchorHTMLAttributes, forwardRef } from 'react'
import { EXCLUDE_ROUTES, REF_PARAM_NAME } from '../utils/constants'

type CustomLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps & {
    children?: React.ReactNode
    preserveRef?: boolean
    excludeRef?: boolean
  }

/**
 * Check if href is a special protocol link that should not have ref added
 */
function isSpecialProtocol(href: string): boolean {
  const specialProtocols = [
    'mailto:',
    'tel:',
    'sms:',
    'fax:',
    'data:',
    'file:',
    'javascript:',
    'blob:',
    'about:',
  ]
  
  return specialProtocols.some(protocol => href.startsWith(protocol))
}

/**
 * Check if href is an external link
 */
function isExternalLink(href: string): boolean {
  return href.startsWith('http://') || 
         href.startsWith('https://') || 
         href.startsWith('//')
}

/**
 * Add ref parameter to internal URL
 */
function addRefToHref(href: string, refValue: string): string {
  try {
    const url = href.startsWith('/')
      ? new URL(href, window.location.origin)
      : new URL(href, window.location.href)

    url.searchParams.set(REF_PARAM_NAME, refValue)
    return url.pathname + url.search + url.hash
  } catch (e) {
    // Fallback for URLs that can't be parsed
    const separator = href.includes('?') ? '&' : '?'
    return `${href}${separator}${REF_PARAM_NAME}=${refValue}`
  }
}

export const Link = forwardRef<HTMLAnchorElement, CustomLinkProps>(function Link(
  { href, preserveRef = true, excludeRef = false, ...props },
  ref
) {
  const searchParams = useSearchParams()
  const refValue = searchParams.get(REF_PARAM_NAME)

  // Early return for non-string hrefs (UrlObject)
  if (typeof href !== 'string') {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Check if we should add ref
  const shouldAddRef = preserveRef && !excludeRef && refValue

  // Early return if we shouldn't add ref
  if (!shouldAddRef) {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Check if href is in exclude list
  const isExcluded = EXCLUDE_ROUTES.some((route) => href.startsWith(route))
  if (isExcluded) {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Skip special protocol links (mailto:, tel:, etc.)
  if (isSpecialProtocol(href)) {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Skip external links
  if (isExternalLink(href)) {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Check if ref already exists
  const hasRef = href.includes(`${REF_PARAM_NAME}=`)
  if (hasRef) {
    return <NextLink ref={ref} href={href} {...props} />
  }

  // Add ref to internal link
  const finalHref = addRefToHref(href, refValue)

  return <NextLink ref={ref} href={finalHref} {...props} />
})

export default Link